import m from "mithril";
import { store } from "../models/store.js";
import { formatMoney, parseNumberInput } from "../services/format.js";
import type { Item } from "../models/types.js";

// The heart of the app: every detected item as a tickable, editable row.
// Tick the ones you're paying for; the running total updates live. Everything is
// editable because OCR is never perfect.

function renderRow(it: Item): m.Vnode {
  const value = store.lineValue(it);
  return m("li.item", { class: it.selected ? "is-selected" : "", key: it.id }, [
    m("input.item-check", {
      type: "checkbox",
      checked: it.selected,
      "aria-label": `Include ${it.name || "item"}`,
      onchange: (ev: Event) => {
        it.selected = (ev.target as HTMLInputElement).checked;
      },
    }),
    m("div.item-fields", [
      m("input.item-name", {
        type: "text",
        value: it.name,
        placeholder: "Item",
        oninput: (ev: Event) => {
          it.name = (ev.target as HTMLInputElement).value;
        },
      }),
      m("div.item-numbers", [
        m("label.mini", [
          "qty",
          m("input.item-qty", {
            type: "text",
            inputmode: "numeric",
            value: it.qty ?? "",
            placeholder: "1",
            oninput: (ev: Event) => {
              it.qty = parseNumberInput((ev.target as HTMLInputElement).value);
            },
          }),
        ]),
        m("label.mini", [
          "unit",
          m("input.item-unit", {
            type: "text",
            inputmode: "decimal",
            value: it.unitPrice ?? "",
            placeholder: "—",
            oninput: (ev: Event) => {
              it.unitPrice = parseNumberInput((ev.target as HTMLInputElement).value);
            },
          }),
        ]),
        m("label.mini", [
          "total",
          m("input.item-total", {
            type: "text",
            inputmode: "decimal",
            value: it.lineTotal ?? "",
            placeholder: "—",
            oninput: (ev: Event) => {
              it.lineTotal = parseNumberInput((ev.target as HTMLInputElement).value);
            },
          }),
        ]),
      ]),
    ]),
    m("span.item-value", formatMoney(value, store.currency)),
    m(
      "button.item-remove",
      { "aria-label": "Remove item", onclick: () => store.removeItem(it.id) },
      "✕",
    ),
  ]);
}

export const ItemList: m.Component = {
  view() {
    if (store.phase !== "ready") return null;
    const hasItems = store.items.length > 0;
    return m("section.items", [
      m("div.items-head", [
        m("h2", "Items"),
        hasItems
          ? m("div.items-bulk", [
              m(
                "button.btn.btn-ghost",
                { onclick: () => store.items.forEach((it) => (it.selected = true)) },
                "All",
              ),
              m(
                "button.btn.btn-ghost",
                { onclick: () => store.items.forEach((it) => (it.selected = false)) },
                "None",
              ),
            ])
          : null,
      ]),
      hasItems
        ? m("ul.item-rows", store.items.map(renderRow))
        : m("p.empty", "No items yet — add them by hand below."),
      m(
        "button.btn.btn-ghost.add-item",
        { onclick: () => store.addBlankItem() },
        "＋ Add item",
      ),
    ]);
  },
};
